import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Menu,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconCloud,
  IconDots,
  IconFilter,
  IconFlag,
  IconLink,
  IconMessage,
  IconMessageCircle,
  IconNote,
  IconPhoto,
  IconPlus,
  IconSend,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  useJournal,
  useCreateReply,
  useUpdateJournalEntry,
  type JournalEntry,
  type SubmissionLink,
} from "@/hooks/useJournal";

interface JournalTimelineProps {
  projectId: string;
  onCreateEntry?: () => void;
  onViewEntry?: (entry: JournalEntry) => void;
}

export function JournalTimeline({ projectId, onCreateEntry, onViewEntry }: JournalTimelineProps) {
  const [filters, setFilters] = useState<{
    source?: string;
    linkId?: string;
    hasIssue?: boolean;
  }>({});

  const { data, isLoading, error } = useJournal(projectId, filters);
  const updateEntry = useUpdateJournalEntry(projectId);

  const handleResolve = async (entryId: string) => {
    await updateEntry.mutateAsync({ entryId, data: { isResolved: true } });
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={120} radius="md" />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper p="xl" withBorder>
        <Text c="red">Failed to load timeline</Text>
      </Paper>
    );
  }

  const entries = data?.entries || [];
  const links = data?.links || [];

  return (
    <Stack gap="md">
      {/* Filters */}
      <Group justify="space-between">
        <Group gap="xs">
          <Select
            placeholder="All sources"
            leftSection={<IconFilter size={16} />}
            size="sm"
            clearable
            data={[
              { value: "internal", label: "Team entries" },
              { value: "link", label: "External submissions" },
            ]}
            value={filters.source || null}
            onChange={(value) => setFilters((f) => ({ ...f, source: value || undefined }))}
            style={{ width: 160 }}
          />

          {links.length > 0 && (
            <Select
              placeholder="All links"
              leftSection={<IconLink size={16} />}
              size="sm"
              clearable
              data={links.map((link) => ({
                value: link.id,
                label: link.name,
              }))}
              value={filters.linkId || null}
              onChange={(value) => setFilters((f) => ({ ...f, linkId: value || undefined }))}
              style={{ width: 160 }}
            />
          )}

          <Button
            variant={filters.hasIssue ? "filled" : "light"}
            size="sm"
            leftSection={<IconFlag size={16} />}
            onClick={() => setFilters((f) => ({ ...f, hasIssue: !f.hasIssue }))}
            color={filters.hasIssue ? "red" : "gray"}
          >
            Issues
          </Button>
        </Group>

        {onCreateEntry && (
          <Button leftSection={<IconPlus size={16} />} onClick={onCreateEntry}>
            Add Entry
          </Button>
        )}
      </Group>

      {/* Empty state */}
      {entries.length === 0 && (
        <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
          <Stack align="center" gap="md">
            <ThemeIcon size={48} variant="light" color="gray">
              <IconNote size={24} />
            </ThemeIcon>
            <Text c="dimmed" ta="center">
              No journal entries yet
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Create your first entry or share a link for external submissions
            </Text>
            {onCreateEntry && (
              <Button leftSection={<IconPlus size={16} />} onClick={onCreateEntry}>
                Create Entry
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Timeline entries */}
      {entries.map((entry) => (
        <JournalEntryCard
          key={entry.id}
          entry={entry}
          projectId={projectId}
          links={links}
          onView={() => onViewEntry?.(entry)}
          onResolve={() => handleResolve(entry.id)}
        />
      ))}

      {/* Pagination */}
      {data?.pagination.hasMore && (
        <Button variant="subtle" fullWidth>
          Load more entries
        </Button>
      )}
    </Stack>
  );
}

interface JournalEntryCardProps {
  entry: JournalEntry;
  projectId: string;
  links: SubmissionLink[];
  onView: () => void;
  onResolve: () => void;
}

function JournalEntryCard({ entry, projectId, links, onView, onResolve }: JournalEntryCardProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const createReply = useCreateReply(projectId, entry.id);

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) return;
    await createReply.mutateAsync(replyContent);
    setReplyContent("");
    setShowReplyForm(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getCategoryColor = () => {
    switch (entry.category) {
      case "issue":
        return "red";
      case "checkpoint":
        return "green";
      case "delivery":
        return "blue";
      case "receipt":
        return "orange";
      default:
        return "gray";
    }
  };

  const sourceLink = entry.sourceLinkId
    ? links.find((l) => l.id === entry.sourceLinkId)
    : null;

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            {/* Avatar/Icon */}
            {entry.source === "internal" && entry.createdByUser ? (
              <Avatar size="sm" radius="xl" color="blue">
                {entry.createdByUser.name?.charAt(0) || "U"}
              </Avatar>
            ) : (
              <Avatar size="sm" radius="xl" color={sourceLink?.color || "grape"}>
                <IconUser size={14} />
              </Avatar>
            )}

            {/* Author info */}
            <div>
              <Group gap="xs">
                <Text size="sm" fw={600}>
                  {entry.source === "internal"
                    ? entry.createdByUser?.name || "Team Member"
                    : entry.submitterName || "External Contributor"}
                </Text>

                {/* Source badge */}
                {entry.source === "link" && sourceLink && (
                  <Badge
                    size="xs"
                    variant="light"
                    color={sourceLink.color || "grape"}
                    leftSection={<IconLink size={10} />}
                  >
                    {sourceLink.name}
                  </Badge>
                )}
              </Group>

              <Group gap="xs">
                <Text size="xs" c="dimmed">
                  {formatDate(entry.createdAt)}
                </Text>
                {entry.referenceNumber && (
                  <Text size="xs" c="dimmed">
                    • {entry.referenceNumber}
                  </Text>
                )}
              </Group>
            </div>
          </Group>

          {/* Actions menu */}
          <Group gap="xs">
            {/* Badges */}
            {entry.hasIssue && !entry.isResolved && (
              <Badge color="red" variant="light" size="sm">
                Issue
              </Badge>
            )}
            {entry.isResolved && (
              <Badge color="green" variant="light" size="sm">
                Resolved
              </Badge>
            )}
            {entry.category && (
              <Badge color={getCategoryColor()} variant="outline" size="sm">
                {entry.category}
              </Badge>
            )}

            <Menu shadow="md" width={160}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconMessage size={14} />} onClick={onView}>
                  View details
                </Menu.Item>
                {entry.hasIssue && !entry.isResolved && (
                  <Menu.Item
                    leftSection={<IconCheck size={14} />}
                    color="green"
                    onClick={onResolve}
                  >
                    Mark resolved
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {/* Title if present */}
        {entry.title && (
          <Text fw={600} size="md">
            {entry.title}
          </Text>
        )}

        {/* Content */}
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
          {entry.content}
        </Text>

        {/* Context info (weather, crew, etc.) */}
        {(entry.weatherTemp || entry.crewCount) && (
          <Group gap="md">
            {entry.weatherTemp && (
              <Group gap={4}>
                <IconCloud size={14} color="gray" />
                <Text size="xs" c="dimmed">
                  {entry.weatherTemp}°F
                  {entry.weatherConditions && ` - ${entry.weatherConditions}`}
                </Text>
              </Group>
            )}
            {entry.crewCount && (
              <Group gap={4}>
                <IconUsers size={14} color="gray" />
                <Text size="xs" c="dimmed">
                  {entry.crewCount} crew
                </Text>
              </Group>
            )}
          </Group>
        )}

        {/* Photos */}
        {entry.photos && entry.photos.length > 0 && (
          <Group gap="xs">
            <IconPhoto size={14} color="gray" />
            <Text size="xs" c="dimmed">
              {entry.photos.length} photo{entry.photos.length !== 1 ? "s" : ""} attached
            </Text>
          </Group>
        )}

        {/* Thread indicator and reply button */}
        <Group justify="space-between" mt="xs">
          <Group gap="xs">
            {entry.replyCount > 0 && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconMessageCircle size={14} />}
                onClick={onView}
              >
                {entry.replyCount} {entry.replyCount === 1 ? "reply" : "replies"}
              </Button>
            )}
          </Group>

          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconMessage size={14} />}
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            Reply
          </Button>
        </Group>

        {/* Inline reply form */}
        {showReplyForm && (
          <Box pt="xs">
            <Group align="flex-end" gap="xs">
              <Textarea
                placeholder="Write a reply..."
                size="sm"
                autosize
                minRows={1}
                maxRows={4}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                style={{ flex: 1 }}
              />
              <ActionIcon
                size="lg"
                variant="filled"
                color="blue"
                onClick={handleSubmitReply}
                loading={createReply.isPending}
                disabled={!replyContent.trim()}
              >
                <IconSend size={16} />
              </ActionIcon>
            </Group>
          </Box>
        )}
      </Stack>
    </Card>
  );
}

// Export for use in route components
export { type JournalEntry };
